"use client"
import React, { useEffect, useMemo, useRef, useState } from 'react'
import HeaderMenu from '@/app/components/HeaderMenu'
import { BlurFade } from '@/app/components/BlurFade'
import { motion, AnimatePresence } from "motion/react"
import { RedirectToMM } from '@/app/components/DeviceRedirect'
import { pick, type Locale } from '@/lib/i18n'
import { commonDict } from '@/lib/i18n/dicts/common'
import { gamelist2Dict, type Gamelist2Dict } from '@/lib/i18n/dicts/gamelist2'
import { categoryLabel, gameCover, type LocalizedGame } from '@/lib/game-i18n'
import { useRegion } from '@/app/hooks/use-region'
import type { Category } from '@/lib/categories'

// "所有游戏"为筛选用的固定中文 key（与 locale 无关），游戏分类匹配也始终用中文 catelog
const ALL_GAMES_KEY = "所有游戏"
// "推荐"虚拟 TAB 的固定 key（后台对游戏打推荐标记；?tab=recommended 可直达）
const RECOMMENDED_KEY = "__recommended"

// 封面：后台上传的列表图（/media/）优先，否则按中文名取 gamelistpic2 默认封面（lib/game-i18n.gameCover）
const coverFor = gameCover

interface Gamelist2ClientProps {
    games: LocalizedGame[]
    categories: Category[]
    locale: Locale
    // 页脚为服务端组件（读库统计），由服务端页面渲染后传入
    footer: React.ReactNode
}

export default function Gamelist2Client({ games: allGames, categories: managedCats, locale, footer }: Gamelist2ClientProps) {
    const t = pick(gamelist2Dict, locale)
    const common = pick(commonDict, locale)
    const [activeCat, setActiveCat] = useState(ALL_GAMES_KEY)
    // 大陆 IP 判定（统一走 useRegion；?isoversea=1 跳过），用于模糊"海外"游戏
    const currentisCN = useRegion() === 'cn'
    // 顶部搜索关键词（客户端过滤，大小写不敏感）
    const [query, setQuery] = useState('')
    // 特性筛选（第二维度）：当前选中的标签，null 表示不筛选；与分类 TAB AND 组合
    const [activeTag, setActiveTag] = useState<string | null>(null)

    // 切换分类时清空特性选择（chips 随当前分类游戏集合重建）
    const handleCatChange = (cat: string) => {
        setActiveCat(cat)
        setActiveTag(null)
    }

    // ?tab=recommended 直达推荐 TAB（后台对游戏打推荐标记后该 TAB 才出现）
    const recommendedGames = useMemo(() => allGames.filter((g) => g.is_recommended), [allGames])
    useEffect(() => {
        if (new URLSearchParams(window.location.search).get('tab') === 'recommended' && recommendedGames.length > 0) {
            setActiveCat(RECOMMENDED_KEY)
        }
    }, [recommendedGames.length])

    // 分类 TAB：所有游戏 → 推荐（有推荐游戏时）→ 分类管理顺序；未登记分类追加在最后
    const categories = useMemo(() => {
        const names = managedCats.map((c) => c.name)
        const leftover = Array.from(new Set(allGames.map((g) => g.catelog)))
            .filter((c) => !names.includes(c))
        const rec = recommendedGames.length > 0 ? [RECOMMENDED_KEY] : []
        return [ALL_GAMES_KEY, ...rec, ...names, ...leftover]
    }, [allGames, managedCats, recommendedGames])

    // ?catelog=分类名 预选分类 TAB（详情页面包屑回跳用；仅应用一次，未登记的分类名忽略）
    const catelogPresetDone = useRef(false)
    useEffect(() => {
        if (catelogPresetDone.current) return
        const cat = new URLSearchParams(window.location.search).get('catelog')
        if (cat && categories.includes(cat)) {
            catelogPresetDone.current = true
            setActiveCat(cat)
        }
    }, [categories])

    // 分类显示名：TAB/空态/卡片徽标统一走这里（未登记分类没有英文名，直接显示中文）
    const catByName = useMemo(() => new Map(managedCats.map((c) => [c.name, c])), [managedCats])
    const labelFor = (cat: string) => {
        if (cat === ALL_GAMES_KEY) return t.tabs.all
        if (cat === RECOMMENDED_KEY) return t.tabs.recommended
        const managed = catByName.get(cat)
        return managed ? categoryLabel(managed, locale) : cat
    }

    // "海外"沉底，其余保持原顺序（稳定排序）；推荐 TAB 取后台标记，其余按中文 catelog 匹配
    const games = useMemo(() => {
        const sorted = [...allGames].sort(
            (a, b) => (a.catelog === "海外" ? 1 : 0) - (b.catelog === "海外" ? 1 : 0)
        )
        if (activeCat === ALL_GAMES_KEY) return sorted
        if (activeCat === RECOMMENDED_KEY) return sorted.filter((g) => g.is_recommended)
        return sorted.filter((g) => g.catelog === activeCat)
    }, [allGames, activeCat])

    // 特性 chips：从当前分类游戏集合的"标签"字段按顿号拆分去重（保持首次出现顺序；字段已本地化）
    const featureTags = useMemo(() => {
        const seen = new Set<string>()
        for (const g of games) {
            for (const raw of (g.标签 || '').split('、')) {
                const tag = raw.trim()
                if (tag) seen.add(tag)
            }
        }
        return Array.from(seen)
    }, [games])

    // 组合过滤：特性与分类 AND；搜索匹配名称/一句话描述/简介（大小写不敏感，字段已本地化故中英都可搜）
    const visibleGames = useMemo(() => {
        let list = games
        if (activeTag) {
            list = list.filter((g) => (g.标签 || '').split('、').map((s) => s.trim()).includes(activeTag))
        }
        const q = query.trim().toLowerCase()
        if (q) {
            list = list.filter((g) =>
                g.name.toLowerCase().includes(q) ||
                g.一句话描述.toLowerCase().includes(q) ||
                g.游戏简介.toLowerCase().includes(q)
            )
        }
        return list
    }, [games, activeTag, query])

    // 榜单区：★推荐榜（后台标记，空榜自动隐藏）；新品以卡片 NEW 角标呈现
    const boards = useMemo(() => {
        return recommendedGames.length > 0
            ? [{ key: 'recommended', title: t.boards.recommended, list: recommendedGames }]
            : []
    }, [recommendedGames, t])

    // 新品角标：created_at 最新 5 款的卡片打 NEW 标
    const newIds = useMemo(() => new Set(
        [...allGames]
            .sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''))
            .slice(0, 5)
            .map((g) => g.gameid)
    ), [allGames])

    return (
        <>
            <RedirectToMM href="/mm/gamelist" />
            <HeaderMenu locale={locale} />

            {/* Hero */}
            <section className="relative overflow-hidden bg-slate-950">
                <div className="pointer-events-none absolute -top-48 left-1/2 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-indigo-600/20 blur-[130px]" />
                <div className="pointer-events-none absolute top-24 right-[-140px] h-[340px] w-[340px] rounded-full bg-cyan-400/10 blur-[110px]" />
                <div className="pointer-events-none absolute bottom-[-120px] left-[-100px] h-[300px] w-[300px] rounded-full bg-fuchsia-500/10 blur-[100px]" />

                <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-20 pb-12 text-center">
                    <BlurFade delay={0.05} inView>
                        <span className="inline-flex items-center gap-2 rounded-full ring-1 ring-indigo-400/30 bg-indigo-500/10 px-4 py-1.5 text-xs font-medium tracking-widest text-indigo-300 uppercase">
                            <span className="h-1.5 w-1.5 rounded-full bg-indigo-400 animate-pulse" />
                            {t.hero.badge}
                        </span>
                    </BlurFade>
                    <BlurFade delay={0.15} inView>
                        <h1 className="mt-6 text-4xl md:text-6xl font-black tracking-tight text-white">
                            {t.hero.titleA}
                            <span className="bg-gradient-to-r from-indigo-400 via-fuchsia-400 to-cyan-300 bg-[length:200%_auto] bg-clip-text text-transparent animate-text-shine">{t.hero.titleB}</span>
                        </h1>
                    </BlurFade>
                    <BlurFade delay={0.25} inView>
                        <p className="mt-5 max-w-2xl mx-auto text-sm md:text-base leading-relaxed text-slate-400">
                            {t.hero.subtitle}
                        </p>
                    </BlurFade>
                    {/* 顶部搜索框：客户端模糊过滤名称/一句话描述/简介 */}
                    <BlurFade delay={0.35} inView>
                        <div className="group relative mt-8 max-w-xl mx-auto">
                            {/* 聚焦时的呼吸光环 */}
                            <span aria-hidden className="pointer-events-none absolute -inset-1 rounded-full bg-indigo-500/25 blur-md opacity-0 transition-opacity group-focus-within:opacity-100 animate-breathe-glow" />
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500">
                                <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35m0 0A7.5 7.5 0 1 0 5.64 5.64a7.5 7.5 0 0 0 10.61 10.61Z" />
                            </svg>
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder={t.search.placeholder}
                                className="w-full rounded-full bg-slate-900/80 py-3 pl-11 pr-10 text-sm text-white ring-1 ring-white/10 outline-none transition-shadow duration-300 placeholder:text-slate-500 focus:ring-indigo-400/60"
                            />
                            {query && (
                                <button
                                    onClick={() => setQuery('')}
                                    aria-label={t.search.clear}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-slate-500 transition-colors hover:text-slate-200"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-4 w-4">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    </BlurFade>
                </div>
            </section>

            {/* 榜单区：Hero 之下、TAB 之上的横滑卡条（空榜自动隐藏） */}
            {boards.length > 0 && (
                <section className="bg-slate-950 border-b border-white/5">
                    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 space-y-9">
                        {boards.map((board) => (
                            <div key={board.key}>
                                <BlurFade delay={0.05} inView>
                                    <h2 className="text-lg md:text-xl font-black tracking-wide text-white">{board.title}</h2>
                                </BlurFade>
                                <div className="mt-4 flex gap-4 overflow-x-auto pb-2 [scrollbar-width:thin]">
                                    {board.list.map((game, i) => (
                                        <RankCard
                                            key={game.gameid}
                                            game={game}
                                            rank={i + 1}
                                            isCN={currentisCN}
                                            t={t}
                                            screenLabel={common.game.screen[game.screen] ?? game.screen}
                                            isNew={newIds.has(game.gameid)}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* 分类筛选 */}
            <section className="bg-slate-950 sticky top-16 z-20">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <div className="flex flex-wrap justify-center gap-2 py-4 border-b border-white/5">
                        {categories.map((cat) => (
                            <button
                                key={cat}
                                onClick={() => handleCatChange(cat)}
                                className={`relative rounded-full px-5 py-2 text-sm font-medium transition-colors duration-300 ${activeCat === cat
                                        ? "text-white"
                                        : "text-slate-400 hover:text-slate-200"
                                    }`}
                            >
                                {activeCat === cat && (
                                    <motion.span
                                        layoutId="cat-pill"
                                        className="absolute inset-0 rounded-full bg-gradient-to-r from-indigo-600 to-fuchsia-600 shadow-lg shadow-indigo-600/30"
                                        transition={{ type: "spring", stiffness: 400, damping: 32 }}
                                    />
                                )}
                                <span className="relative z-10">
                                    {cat === RECOMMENDED_KEY && <span className="mr-1 text-amber-400">★</span>}
                                    {labelFor(cat)}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* 特性筛选（第二维度）：chips 来自当前分类游戏集合的标签，单选、再点取消；与分类 AND 组合，cyan 配色区分 */}
                    {featureTags.length > 0 && (
                        <div className="flex flex-wrap items-center justify-center gap-2 py-3 border-b border-white/5">
                            <span className="mr-1 text-xs font-medium text-slate-500">{t.features.label}</span>
                            {featureTags.map((tag) => (
                                <button
                                    key={tag}
                                    onClick={() => setActiveTag(activeTag === tag ? null : tag)}
                                    className={`relative rounded-full px-4 py-1.5 text-xs font-medium transition-colors duration-300 ${activeTag === tag
                                            ? "text-white"
                                            : "text-slate-400 hover:text-slate-200"
                                        }`}
                                >
                                    {activeTag === tag && (
                                        <motion.span
                                            layoutId="tag-pill"
                                            className="absolute inset-0 rounded-full bg-gradient-to-r from-cyan-500 to-cyan-400 shadow-lg shadow-cyan-500/30"
                                            transition={{ type: "spring", stiffness: 400, damping: 32 }}
                                        />
                                    )}
                                    <span className="relative z-10">{tag}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* 游戏网格（带筛选布局动画） */}
            <section className="bg-slate-950 min-h-[480px] pb-20 pt-8">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    {visibleGames.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-32 text-center">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.2} stroke="currentColor" className="h-12 w-12 text-slate-600">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 1 0 5.25 5.25a7.5 7.5 0 0 0 11.4 11.4Z" />
                            </svg>
                            {query.trim() ? (
                                <>
                                    <p className="mt-4 text-sm font-medium text-slate-400">{t.search.emptyTitle.replace('{q}', query.trim())}</p>
                                    <p className="mt-1 text-xs text-slate-600">{t.search.emptyHint}</p>
                                </>
                            ) : (
                                <>
                                    <p className="mt-4 text-sm font-medium text-slate-400">{t.empty.title.replace('{cat}', labelFor(activeCat))}</p>
                                    <p className="mt-1 text-xs text-slate-600">{t.empty.hint}</p>
                                </>
                            )}
                        </div>
                    ) : (
                        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            <AnimatePresence mode="popLayout">
                                {visibleGames.map((game) => (
                                    <GameCard
                                        key={game.gameid}
                                        game={game}
                                        isCN={currentisCN}
                                        t={t}
                                        viewDetail={common.game.viewDetail}
                                        screenLabel={common.game.screen[game.screen] ?? game.screen}
                                        catLabel={labelFor(game.catelog)}
                                        isNew={newIds.has(game.gameid)}
                                    />
                                ))}
                            </AnimatePresence>
                        </motion.div>
                    )}
                </div>
            </section>

            {footer}
        </>
    )
}

interface GameCardProps {
    game: LocalizedGame
    isCN: boolean
    t: Gamelist2Dict
    viewDetail: string
    screenLabel: string
    catLabel: string
    isNew?: boolean
}

function GameCard({ game, isCN, t, viewDetail, screenLabel, catLabel, isNew = false }: GameCardProps) {
    const blocked = isCN && game.catelog === "海外"

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.92, transition: { duration: 0.2 } }}
            transition={{ type: "spring", stiffness: 260, damping: 28 }}
            className="group relative h-full"
        >
            {/* hover 光晕描边（卡片背后的渐变柔光） */}
            <div aria-hidden className="pointer-events-none absolute -inset-px rounded-2xl bg-gradient-to-br from-indigo-500/40 via-fuchsia-500/25 to-cyan-400/40 blur-md opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
            <a
                href={blocked ? "#" : `../gamedetail2/${game.gameid}`}
                className="relative block h-full rounded-2xl bg-slate-900 ring-1 ring-white/10 overflow-hidden transition-all duration-300 ease-out hover:-translate-y-1.5 hover:ring-indigo-400/60 hover:shadow-2xl hover:shadow-indigo-500/25"
            >
                {/* hover 微光扫过 */}
                <span aria-hidden className="pointer-events-none absolute inset-0 z-30 -translate-x-[130%] skew-x-12 bg-gradient-to-r from-transparent via-white/10 to-transparent transition-transform duration-1000 ease-out group-hover:translate-x-[130%]" />
                {/* 封面 */}
                <div className="relative aspect-[4/3] overflow-hidden">
                    {blocked && (
                        <span className="absolute inset-0 z-20 flex items-center justify-center px-6 text-center text-sm font-bold text-white bg-slate-950/40">
                            {t.overseaBlocked}
                        </span>
                    )}
                    <img
                        src={coverFor(game)}
                        alt={game.name}
                        loading="lazy"
                        onError={(e) => { e.currentTarget.src = game.imageUrl }}
                        className={`h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110 ${blocked ? "blur-md" : ""}`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent" />

                    <span className="absolute top-3 left-3 flex items-center gap-1.5">
                        {isNew && (
                            <span className="rounded-full bg-emerald-500/90 backdrop-blur px-2 py-1 text-[10px] font-black tracking-wider text-white shadow-lg shadow-emerald-500/30">
                                NEW
                            </span>
                        )}
                        <span className="rounded-full bg-indigo-500/80 backdrop-blur px-3 py-1 text-[11px] font-medium text-white">
                            {catLabel}
                        </span>
                    </span>
                    <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-slate-950/60 backdrop-blur px-2.5 py-1 text-[11px] font-medium text-slate-200 ring-1 ring-white/15">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={`h-3.5 w-3.5 ${game.screen === "竖版" ? "" : "rotate-90"}`}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5a2.25 2.25 0 0 0 2.25-2.25V3.75A2.25 2.25 0 0 0 15.75 1.5H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
                        </svg>
                        {screenLabel}
                    </span>
                </div>

                {/* 信息区 */}
                <div className="flex flex-col p-5">
                    <h3 className="text-lg font-bold text-white group-hover:text-indigo-300 transition-colors duration-300">
                        {game.name}
                    </h3>
                    <p className="mt-2 text-xs leading-5 text-slate-400 line-clamp-2 min-h-[2.5rem]">
                        {game.一句话描述 || game.游戏简介}
                    </p>

                    {game.标签 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                            {game.标签.split('、').slice(0, 3).map((tag) => (
                                <span key={tag} className="rounded-md bg-white/5 ring-1 ring-white/10 px-2 py-0.5 text-[11px] text-slate-300">
                                    {tag}
                                </span>
                            ))}
                        </div>
                    )}

                    <div className="mt-4 flex items-center text-xs font-medium text-indigo-400">
                        {viewDetail}
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="ml-1 h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                        </svg>
                    </div>
                </div>
            </a>
        </motion.div>
    )
}

interface RankCardProps {
    game: LocalizedGame
    /** 名次（1 起），前三名高亮 */
    rank: number
    isCN: boolean
    t: Gamelist2Dict
    screenLabel: string
    isNew?: boolean
}

// 榜单紧凑卡：GameCard 的固定宽度变体（无布局动效，避免与横滑容器动画冲突），沿用封面/徽标/IP 遮罩逻辑
function RankCard({ game, rank, isCN, t, screenLabel, isNew = false }: RankCardProps) {
    const blocked = isCN && game.catelog === "海外"

    return (
        <div className="group relative w-40 shrink-0">
            {/* hover 光晕描边 */}
            <div aria-hidden className="pointer-events-none absolute -inset-px rounded-2xl bg-gradient-to-br from-indigo-500/40 via-fuchsia-500/25 to-cyan-400/40 blur-md opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
            <a
                href={blocked ? "#" : `../gamedetail2/${game.gameid}`}
                className="relative block rounded-2xl bg-slate-900 ring-1 ring-white/10 overflow-hidden transition-all duration-300 ease-out hover:-translate-y-1.5 hover:ring-indigo-400/60 hover:shadow-2xl hover:shadow-indigo-500/25"
            >
                {/* 封面 */}
                <div className="relative aspect-[4/3] overflow-hidden">
                    {blocked && (
                        <span className="absolute inset-0 z-20 flex items-center justify-center px-3 text-center text-[11px] font-bold text-white bg-slate-950/40">
                            {t.overseaBlocked}
                        </span>
                    )}
                    <img
                        src={coverFor(game)}
                        alt={game.name}
                        loading="lazy"
                        onError={(e) => { e.currentTarget.src = game.imageUrl }}
                        className={`h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110 ${blocked ? "blur-md" : ""}`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent" />

                    {/* 名次徽标（前三名金橙高亮；榜首呼吸光晕） */}
                    <span className={`absolute top-2 left-2 inline-flex h-6 min-w-6 items-center justify-center rounded-full px-1.5 text-[11px] font-black shadow-lg ${rank <= 3
                            ? "bg-gradient-to-br from-amber-400 to-orange-500 text-slate-950 shadow-orange-500/30"
                            : "bg-slate-950/70 text-slate-200 ring-1 ring-white/15"
                        } ${rank === 1 ? "animate-breathe" : ""}`}>
                        {rank}
                    </span>
                    {isNew && (
                        <span className="absolute top-2 left-9 rounded-full bg-emerald-500/90 backdrop-blur px-1.5 py-0.5 text-[10px] font-black tracking-wider text-white shadow-lg shadow-emerald-500/30">
                            NEW
                        </span>
                    )}
                    <span className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full bg-slate-950/60 backdrop-blur px-2 py-0.5 text-[10px] font-medium text-slate-200 ring-1 ring-white/15">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={`h-3 w-3 ${game.screen === "竖版" ? "" : "rotate-90"}`}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5a2.25 2.25 0 0 0 2.25-2.25V3.75A2.25 2.25 0 0 0 15.75 1.5H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
                        </svg>
                        {screenLabel}
                    </span>
                </div>

                {/* 信息区 */}
                <div className="p-3">
                    <h3 className="truncate text-sm font-bold text-white group-hover:text-indigo-300 transition-colors duration-300">
                        {game.name}
                    </h3>
                    <p className="mt-1 truncate text-[11px] text-slate-500">
                        {game.一句话描述 || game.游戏简介}
                    </p>
                </div>
            </a>
        </div>
    )
}
